import {
  Button,
  ButtonProps,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { PropsWithChildren } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "services/ApiClient";
import { generatePath } from "react-router";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { newHireJourneyKeys } from "features/NewHireJourney/hooks";
import { NewHireJourney } from "models/v2/newHireJourney";
import NewHireJourneyDocsForm from "../NewHireJourney/AddOnboardingPlanForm";
import NewHireJourneyForm from "../NewHireJourney/NewHireJourneyForm";

interface NewHireJourneyEditButtonWithModalProps extends PropsWithChildren, ButtonProps {
  newHireJourney: NewHireJourney | undefined;
  formType?: "RESOURCES";
}

export default function NewHireJourneyEditButtonWithModal({
  newHireJourney,
  children,
  formType = undefined,
  ...rest
}: NewHireJourneyEditButtonWithModalProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast({
    status: "success",
  });
  const queryClient = useQueryClient();

  const initialRef = React.useRef(null);

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (formData: any) => {
      const res = await apiClient.put<NewHireJourney>(
        generatePath(API_ROUTES.newHireJourneys.show, { id: newHireJourney?.id }),
        {
          newHireJourney: formData,
        },
      );
      return res;
    },
    onSuccess: async (res) => {
      queryClient.setQueryData(newHireJourneyKeys.detail(res.id), (oldData: NewHireJourney | undefined) => {
        if (!oldData) {
          return res;
        }
        return {
          ...oldData,
          ...res,
        };
      });
      toast({ title: "Updated" });
      onClose();
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });

  let Form;
  const modalSize = "3xl";
  switch (formType) {
    case "RESOURCES":
      Form = NewHireJourneyDocsForm;
      break;
    default:
      Form = NewHireJourneyForm;
      break;
  }
  return (
    <>
      {React.cloneElement(children as JSX.Element, { onClick: onOpen, ...rest }) || (
        <Button onClick={onOpen} {...rest}>
          Update Onboarding Plan
        </Button>
      )}

      <Modal
        initialFocusRef={initialRef}
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={onClose}
        size={modalSize}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {Form && <Form data={newHireJourney} isLoading={isLoading} onFormSubmit={mutate} />}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
